class CustomSelect extends HTMLElement {
  static get observedAttributes() {
    return ["selected"];
  }

  /** Collection sort options shown in the UI (Shopify sort_by values). */
  static SORT_OPTIONS = [
    { value: "manual", label: "Recommended" },
    { value: "best-selling", label: "Best Selling" },
    { value: "created-descending", label: "New Arrivals" },
    { value: "price-ascending", label: "Price: Low to High" },
    { value: "price-descending", label: "Price: High to Low" },
  ];

  constructor() {
    super();
    this.referenceElement = document.getElementById(this.dataset.referenceId);
    this.currentLabel = this.querySelector("[custom-select-current]");
    this.mainTrigger = this.querySelector("[custom-select-trigger]");
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {}

  attributeChangedCallback(name, oldValue, newValue) {
    const onChangeEvent = new CustomEvent("wtSelectChange", {
      detail: { prevValue: oldValue, value: newValue },
    });
    this.dispatchEvent(onChangeEvent);
  }

  getFocusableElements() {
    const focusableElementsSelector = "[role='option']";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );
    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  setSelectedLabel() {
    const selectedLabel = this.querySelector(
      "[custom-select-list] [selected]",
    )?.textContent?.trim();
    this.setAttribute("selected-label", selectedLabel || "");
    if (this.currentLabel) this.currentLabel.textContent = selectedLabel || "";
  }

  getAttrVal(attr, elem) {
    return elem?.hasAttribute?.(attr) ? elem.getAttribute(attr) : null;
  }

  unselectAll() {
    this.querySelectorAll("[custom-select-list] li").forEach((item) =>
      item.removeAttribute("selected"),
    );
  }

  /** Keep only the five Sort By options Bobby wants. */
  applySortOptions() {
    if (this.dataset.referenceId !== "SortBy") return;
    const list = this.querySelector("[custom-select-list]");
    if (!list) return;

    const current =
      this.getAttribute("selected") ||
      this.referenceElement?.value ||
      CustomSelect.SORT_OPTIONS[0].value;

    const allowed = new Set(CustomSelect.SORT_OPTIONS.map((o) => o.value));
    const selectedValue = allowed.has(current)
      ? current
      : CustomSelect.SORT_OPTIONS[0].value;

    list.innerHTML = CustomSelect.SORT_OPTIONS.map((opt) => {
      const selected = opt.value === selectedValue ? " selected" : "";
      return `<li value="${opt.value}" class="wt-options__list__item" role="option" tabindex="-1"${selected}>
            ${opt.label}
          </li>`;
    }).join("");

    this.setAttribute("selected", selectedValue);
    if (this.referenceElement) this.referenceElement.value = selectedValue;
    this.setSelectedLabel();
  }

  onSelect(val, elem) {
    this.unselectAll();
    this.setAttribute("selected", val);
    elem.setAttribute("selected", "");
    this.setSelectedLabel();
    this.updateReferenceValue();
    this.toggleState();
  }

  createOverlay() {
    if (!document.querySelector("[custom-select-overlay]")) {
      const overlay = document.createElement("div");
      overlay.setAttribute("custom-select-overlay", "");
      document.body.appendChild(overlay);
      overlay.addEventListener("click", () => this.toggleState());
    }
  }

  handleClick(e) {
    const closeEl = e.target.closest?.(".wt-options__close");
    const optionEl = e.target.closest?.("[custom-select-list] [value]");
    const clickedDrawer = e.target.closest?.("[custom-select-drawer]");
    const clickedOnDrawerChrome =
      e.target.hasAttribute?.("custom-select-drawer") ||
      e.target === this ||
      closeEl;

    if (clickedOnDrawerChrome && !optionEl) {
      // Backdrop / close only (not when picking an option inside the drawer)
      if (closeEl || e.target.hasAttribute?.("custom-select-drawer") || e.target === this) {
        this.toggleState();
      }
      return;
    }

    if (optionEl) {
      const val = this.getAttrVal("value", optionEl);
      if (val) this.onSelect(val, optionEl);
    }
  }

  init() {
    this.applySortOptions();
    this.createOverlay();
    this.mainTrigger?.addEventListener("click", () => this.toggleState());
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();
      if (
        (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") &&
        this.hasAttribute("open")
      ) {
        this.toggleState();
      }
      if (isTabPressed && this.hasAttribute("open")) {
        if (e.shiftKey && document.activeElement === first) {
          last?.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first?.focus();
          e.preventDefault();
        }
      }
    });
    this.addEventListener("click", this.handleClick.bind(this));
    this.setSelectedLabel();
  }

  updateReferenceValue() {
    if (!this.referenceElement) return;
    this.referenceElement.value = this.getAttribute("selected");
    const inputEvent = new Event("input", { bubbles: true, cancelable: true });
    this.referenceElement.dispatchEvent(inputEvent);
  }

  toggleState() {
    const body = document.body;
    const openBodyAttr = "custom-select-opened";
    const header = document.querySelector("header.page-header");
    const liItems = this.querySelectorAll("li");
    const trigger = this.querySelector("[custom-select-trigger]");
    const isOpen = trigger?.getAttribute("aria-expanded") === "true";

    trigger?.setAttribute("aria-expanded", String(!isOpen));

    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      body.removeAttribute(openBodyAttr);
      if (body.classList.contains("page-header-sticky")) {
        header?.classList.remove("hide");
      }
      for (const item of liItems) item.setAttribute("tabindex", "-1");
      this.mainTrigger?.focus();
    } else {
      this.setAttribute("open", "");
      body.setAttribute(openBodyAttr, "");
      if (body.classList.contains("page-header-sticky")) {
        header?.classList.add("hide");
      }
      for (const item of liItems) item.setAttribute("tabindex", "0");
      if (window.matchMedia("(max-width: 1199px)").matches) {
        this.querySelector("[custom-select-list] [selected]")?.focus();
      }
    }
  }
}

customElements.define("custom-select", CustomSelect);
